import { useState, useEffect } from 'react';
import {
    Box, Text, Flex, Grid, Progress, VStack, HStack, Spinner, useToast,
    Icon, Badge, Divider, SimpleGrid,
} from '@chakra-ui/react';
import {
    FiZap, FiTrendingUp, FiClock, FiActivity, FiMessageCircle, FiEye,
    FiBarChart2, FiTarget, FiAlertTriangle, FiCpu, FiArrowDown,
} from 'react-icons/fi';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getApiHeaders } from '../../../utils/DefaultFunction';
import API from '../../../helpers/axiosInstance';

const ACCENT = '#4f5ed1';

// Engagement band → temperature label + colour (matches the Figma "Hot" badge).
const BAND = {
    highly_engaged: { label: 'Hot', color: 'green' },
    moderately_engaged: { label: 'Warm', color: 'blue' },
    weak_engaged: { label: 'Cool', color: 'orange' },
    at_risk: { label: 'At Risk', color: 'red' },
};
// Per-channel accent colour, keyed by display label.
const CH_COLOR = {
    WhatsApp: '#22c55e', SMS: '#3b82f6', Email: '#ef4444',
    Push: '#a855f7', 'In-App': '#14b8a6', Voice: '#f59e0b',
};
const chColor = (label) => CH_COLOR[label] || '#64748b';
const toneColor = { green: 'green.500', orange: 'orange.500', red: 'red.500', blue: 'blue.500', gray: 'gray.500' };
const HC_COLORS = { WhatsApp: '#22c55e', SMS: '#3b82f6', Email: '#ef4444', Push: '#a855f7', 'In-App': '#14b8a6' };

const Card = (props) => (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" bg="white" p={5} {...props} />
);
const CardTitle = ({ children, sub }) => (
    <Box mb={4}>
        <Text fontWeight="700" fontSize="md">{children}</Text>
        {sub && <Text fontSize="sm" color="gray.500" mt={0.5}>{sub}</Text>}
    </Box>
);

// Full-width inline SVG sparkline for the engagement-score card (scales to container).
function Sparkline({ points = [], color = '#22c55e', h = 32 }) {
    if (!points.length) return null;
    const W = 300; // viewBox width; svg stretches to 100% of the card
    const max = Math.max(...points, 1), min = Math.min(...points, 0);
    const span = max - min || 1;
    const step = W / Math.max(points.length - 1, 1);
    const d = points.map((p, i) => `${i * step},${h - ((p - min) / span) * h}`).join(' ');
    return (
        <svg viewBox={`0 0 ${W} ${h}`} width="100%" height={h} preserveAspectRatio="none" style={{ display: 'block' }}>
            <polyline points={d} fill="none" stroke={color} strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>
    );
}

export function OverviewTab({ id }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const toast = useToast();

    useEffect(() => {
        let alive = true;
        setLoading(true);
        API.get(`${process.env.REACT_APP_API_URL}/contacts/${id}/engagement-overview`, { headers: getApiHeaders() })
            .then((r) => { if (alive) setData(r?.data?.data || null); })
            .catch((e) => {
                console.error('engagement-overview error:', e);
                if (alive) { setData(null); toast({ title: 'Error', description: 'Failed to load overview', status: 'error', duration: 3000, isClosable: true }); }
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (loading) return <Flex justify="center" align="center" minH="400px"><Spinner size="xl" color={ACCENT} /></Flex>;
    if (!data) return <Flex justify="center" align="center" minH="400px"><Text color="gray.500">No data available</Text></Flex>;

    if (!data.computed) {
        return (
            <Card mt={4} textAlign="center" py={12}>
                <Icon as={FiActivity} boxSize={8} color="gray.300" />
                <Text mt={3} fontWeight="600">No engagement signal yet</Text>
                <Text fontSize="sm" color="gray.500">This contact has no delivered / read / clicked messages in the last 30 days.</Text>
            </Card>
        );
    }

    const eng = data.engagement;
    const aff = data.affinity;
    const rb = data.read_behavior;
    const bd = data.breakdown;
    const sig = data.behavioral_signals;
    const ai = data.ai_summary;
    const band = BAND[eng.band] || { label: '—', color: 'gray' };
    const trendUp = (eng.trend_pct_7d || 0) >= 0;

    // ── charts ──
    const trendOptions = {
        chart: { type: 'spline', height: 240, spacingTop: 10 },
        title: { text: null }, credits: { enabled: false }, legend: { enabled: false },
        xAxis: { categories: data.trend_30d.map((p) => p.label), tickInterval: 4, labels: { style: { fontSize: '10px', color: '#94a3b8' } }, lineColor: '#e2e8f0' },
        yAxis: { title: { text: null }, min: 0, max: 100, gridLineColor: '#f1f5f9', labels: { style: { fontSize: '10px', color: '#94a3b8' } } },
        tooltip: { valueSuffix: ' pts' },
        plotOptions: { spline: { marker: { radius: 3 }, lineWidth: 2 } },
        series: [{ name: 'Engagement', data: data.trend_30d.map((p) => p.score), color: ACCENT }],
    };
    const actOptions = {
        chart: { type: 'spline', height: 240, spacingTop: 10 },
        title: { text: null }, credits: { enabled: false },
        legend: { align: 'center', verticalAlign: 'bottom', itemStyle: { fontSize: '11px' } },
        xAxis: { categories: data.activity_vs_reads.map((p) => p.label), tickInterval: 4, labels: { style: { fontSize: '10px', color: '#94a3b8' } }, lineColor: '#e2e8f0' },
        yAxis: { title: { text: null }, min: 0, gridLineColor: '#f1f5f9', labels: { style: { fontSize: '10px', color: '#94a3b8' } } },
        plotOptions: { spline: { marker: { radius: 2 }, lineWidth: 2 } },
        series: [
            { name: 'Messages Sent', data: data.activity_vs_reads.map((p) => p.sent), color: ACCENT },
            { name: 'Messages Read', data: data.activity_vs_reads.map((p) => p.read), color: '#22c55e' },
        ],
    };

    const behaviorRow = (label, value, color) => (
        <Flex justify="space-between" align="center" py={1.5}>
            <Text fontSize="sm" color="gray.600">{label}</Text>
            <Text fontSize="sm" fontWeight="700" color={color || 'gray.800'}>{value ?? '—'}</Text>
        </Flex>
    );

    const SignalTile = ({ icon, label, value, sub, tone }) => (
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4} bg="gray.50">
            <Flex justify="space-between" align="start">
                <Text fontSize="xs" color="gray.500" fontWeight="600">{label}</Text>
                <Icon as={icon} color="gray.400" boxSize={4} />
            </Flex>
            <Text fontSize="lg" fontWeight="800" mt={1} color={tone ? toneColor[tone] : 'gray.800'}>{value}</Text>
            <Text fontSize="xs" color="gray.500">{sub}</Text>
        </Box>
    );

    return (
        <VStack align="stretch" spacing={5} mt={4}>
            {/* ── Row 1: four score cards ── */}
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }} gap={4}>
                {/* Engagement Score */}
                <Card>
                    <Flex justify="space-between" align="center">
                        <Text fontWeight="700" fontSize="sm">Engagement Score</Text>
                        <Box w="10px" h="10px" borderRadius="full" bg={`${band.color}.400`} />
                    </Flex>
                    <Flex align="center" gap={3} mt={4}>
                        <Text fontSize="4xl" fontWeight="800" lineHeight="1" color={`${band.color}.600`}>{eng.score}</Text>
                        <Badge colorScheme={band.color} borderRadius="full" px={3} py={1} fontSize="sm">{band.label}</Badge>
                    </Flex>
                    <HStack spacing={1.5} mt={3}>
                        <Icon as={trendUp ? FiTrendingUp : FiArrowDown} boxSize={4} color={trendUp ? 'green.500' : 'red.500'} />
                        <Text fontSize="sm" fontWeight="700" color={trendUp ? 'green.500' : 'red.500'}>{trendUp ? '+' : ''}{eng.trend_pct_7d}%</Text>
                        <Text fontSize="sm" color="gray.500">last 7 days</Text>
                    </HStack>
                    <Box mt={4} mb={1}><Sparkline points={eng.sparkline} color={trendUp ? '#22c55e' : '#ef4444'} /></Box>
                    <Text fontSize="xs" color="gray.400" mt={4} fontStyle="italic">Based on read rate, responsiveness, recency &amp; interaction frequency</Text>
                </Card>

                {/* Channel Affinity */}
                <Card>
                    <Text fontWeight="700" fontSize="sm">Channel Affinity</Text>
                    <Flex align="center" gap={2} mt={2} mb={1}>
                        <Box w="8px" h="8px" borderRadius="full" bg={chColor(aff.top_channel)} />
                        <Text fontWeight="700" fontSize="sm">{aff.top_channel || '—'}</Text>
                        {aff.top_channel && <Badge colorScheme="green" fontSize="10px" borderRadius="full" px={2}>{aff.confidence}</Badge>}
                    </Flex>
                    <Text fontSize="2xl" fontWeight="800" mb={2}>{aff.top_score?.toFixed(2)}</Text>
                    <VStack align="stretch" spacing={2}>
                        {aff.channels.map((c) => (
                            <Box key={c.channel}>
                                <Flex justify="space-between" mb={0.5}>
                                    <Text fontSize="xs" color="gray.600">{c.label}</Text>
                                    <Text fontSize="xs" fontWeight="700">{Math.round(c.value * 100)}%</Text>
                                </Flex>
                                <Progress value={c.value * 100} size="xs" borderRadius="full" bg="gray.100"
                                    sx={{ '& > div': { background: chColor(c.label) } }} />
                            </Box>
                        ))}
                    </VStack>
                    <Text fontSize="11px" color="gray.400" mt={3}>Based on read rate, read speed &amp; recency</Text>
                </Card>

                {/* Read Behavior */}
                <Card>
                    <Text fontWeight="700" fontSize="sm" mb={2}>Read Behavior</Text>
                    {behaviorRow('Most Active', rb.most_active_channel, chColor(rb.most_active_channel))}
                    {behaviorRow('Median Read', rb.median_read_label)}
                    {behaviorRow('Average Read', rb.avg_read_label)}
                    {behaviorRow('Fastest', rb.fastest_channel, chColor(rb.fastest_channel))}
                    {behaviorRow('Slowest', rb.slowest_channel, chColor(rb.slowest_channel))}
                    {rb.peak_window_label && (
                        <Box mt={2} bg="blue.50" borderRadius="md" p={3}>
                            <HStack spacing={2} mb={1}><Icon as={FiClock} color="blue.500" boxSize={3.5} /><Text fontSize="xs" color="gray.600" fontWeight="600">Peak Read Window</Text></HStack>
                            <Text fontSize="md" fontWeight="800" color="blue.600">{rb.peak_window_label}</Text>
                        </Box>
                    )}
                </Card>

                {/* Engagement Breakdown */}
                <Card>
                    <Text fontWeight="700" fontSize="sm" mb={2}>Engagement Breakdown</Text>
                    {behaviorRow('Messages Sent (30d)', bd.sent_30d)}
                    {behaviorRow('Messages Read', bd.read_30d, 'green.500')}
                    {behaviorRow('Read Rate', `${bd.read_rate_pct}%`, 'blue.500')}
                    {behaviorRow('Click/Reply Rate', `${bd.click_reply_rate_pct}%`)}
                    {behaviorRow('Last Engagement', bd.last_engagement_label)}
                    <Box mt={2} bg="orange.50" borderRadius="md" p={3}>
                        <Flex justify="space-between" align="center">
                            <HStack spacing={2}><Icon as={FiAlertTriangle} color="orange.500" boxSize={3.5} /><Text fontSize="xs" color="gray.600" fontWeight="600">Fatigue Index</Text></HStack>
                            <Badge colorScheme={bd.fatigue_index === 'Low' ? 'green' : bd.fatigue_index === 'Medium' ? 'orange' : 'red'} borderRadius="full" px={2}>{bd.fatigue_index}</Badge>
                        </Flex>
                        <Text fontSize="11px" color="gray.500" mt={1}>Send frequency vs engagement</Text>
                    </Box>
                </Card>
            </Grid>

            {/* ── Row 2: Channel Performance Analysis ── */}
            <Card>
                <CardTitle sub="Comprehensive channel behavior and affinity metrics">Channel Performance Analysis</CardTitle>
                <Box overflowX="auto">
                    <Box minW="640px">
                        <Grid templateColumns="1.4fr 1fr 1.6fr 1.2fr 1fr" px={2} pb={2} borderBottomWidth="1px" borderColor="gray.100">
                            {['Channel', 'Delivered', 'Read %', 'Median Read Time', 'Affinity Score'].map((h) => (
                                <Text key={h} fontSize="xs" color="gray.500" fontWeight="600" textAlign="left">{h}</Text>
                            ))}
                        </Grid>
                        {data.channel_performance.map((c) => (
                            <Grid key={c.channel} templateColumns="1.4fr 1fr 1.6fr 1.2fr 1fr" px={2} py={3} alignItems="center"
                                bg={c.is_top ? 'blue.50' : 'transparent'} borderRadius="md">
                                <HStack spacing={2}>
                                    <Box w="8px" h="8px" borderRadius="full" bg={chColor(c.channel)} />
                                    <Text fontSize="sm" fontWeight="600">{c.channel}</Text>
                                    {c.is_top && <Badge colorScheme="blue" fontSize="9px" borderRadius="full">Top</Badge>}
                                </HStack>
                                
                                <Text fontSize="sm" fontWeight="700">{c.delivered}</Text>
                                <HStack spacing={2}>
                                    <Progress value={c.read_pct} size="sm" w="70px" borderRadius="full" bg="gray.100"
                                        sx={{ '& > div': { background: chColor(c.channel) } }} />
                                    <Text fontSize="sm" fontWeight="700" color="green.600">{c.read_pct}%</Text>
                                </HStack>
                                <Text fontSize="sm" color="gray.700">{c.median_read_label}</Text>
                                <Text fontSize="sm" fontWeight="700" color={c.is_top ? 'blue.600' : 'gray.400'}>{c.affinity_score.toFixed(2)}</Text>
                            </Grid>
                        ))}
                    </Box>
                </Box>
                {data.routing_optimized_for && (
                    <Flex mt={3} bg="gray.50" borderRadius="md" p={3} align="center" gap={2}>
                        <Icon as={FiActivity} color={ACCENT} boxSize={4} />
                        <Text fontSize="sm" color="gray.600">System routing priority currently optimized for{' '}
                            <Text as="span" fontWeight="700" color={chColor(data.routing_optimized_for)}>{data.routing_optimized_for}</Text>
                        </Text>
                    </Flex>
                )}
            </Card>

            {/* ── Row 3: two charts ── */}
            <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={4}>
                <Card>
                    <CardTitle sub="Daily engagement score evolution">30-Day Engagement Score Trend</CardTitle>
                    <HighchartsReact highcharts={Highcharts} options={trendOptions} />
                </Card>
                <Card>
                    <CardTitle sub="Sent vs read comparison over 30 days">Message Activity vs Reads</CardTitle>
                    <HighchartsReact highcharts={Highcharts} options={actOptions} />
                </Card>
            </Grid>

            {/* ── Row 4: Behavioral Insight banner ── */}
            <Flex bg="blue.50" borderWidth="1px" borderColor="blue.100" borderRadius="lg" p={4} gap={3} align="start">
                <Icon as={FiTrendingUp} color="blue.500" boxSize={5} mt={0.5} />
                <Box>
                    <Text fontWeight="700" fontSize="sm" mb={0.5}>Behavioral Insight</Text>
                    <Text fontSize="sm" color="gray.700">
                        {aff.top_channel ? <>Strongest channel is <b>{aff.top_channel}</b> at {sig.channel_preference.sub}. </> : null}
                        Read consistency is <b>{sig.consistency.value.toLowerCase()}</b> with <b>{sig.volatility.value.toLowerCase()}</b> volatility.
                        {' '}Engagement trend over the last 7 days is <b>{trendUp ? 'improving' : 'declining'}</b> ({trendUp ? '+' : ''}{eng.trend_pct_7d}%).
                    </Text>
                </Box>
            </Flex>

            {/* ── Row 5: Behavioral Signals ── */}
            <Card>
                <CardTitle sub="Key behavioral patterns and indicators">Behavioral Signals</CardTitle>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <SignalTile icon={FiMessageCircle} label="Channel Preference" value={sig.channel_preference.value} sub={sig.channel_preference.sub} />
                    <SignalTile icon={FiClock} label="Interaction Pattern" value={sig.interaction_pattern.value} sub={sig.interaction_pattern.sub} />
                    <SignalTile icon={FiActivity} label="Consistency" value={sig.consistency.value} sub={sig.consistency.sub} tone={sig.consistency.tone} />
                    <SignalTile icon={FiBarChart2} label="Volatility" value={sig.volatility.value} sub={sig.volatility.sub} tone={sig.volatility.tone} />
                    <SignalTile icon={FiClock} label="Attention Span" value={sig.attention_span.value} sub={sig.attention_span.sub} />
                    <SignalTile icon={FiEye} label="Saturation Risk" value={sig.saturation_risk.value} sub={sig.saturation_risk.sub} tone={sig.saturation_risk.tone} />
                </SimpleGrid>
            </Card>

            {/* ── Row 6: AI Intelligence Summary ── */}
            <Box borderWidth="1px" borderColor="purple.100" bg="purple.50" borderRadius="lg" p={5}>
                <Flex align="center" gap={2} mb={1}>
                    <Flex bg={ACCENT} borderRadius="md" p={1.5}><Icon as={FiCpu} color="white" boxSize={4} /></Flex>
                    <Text fontWeight="700">AI Intelligence Summary</Text>
                </Flex>
                <Text fontSize="sm" color={ACCENT} mb={3} fontWeight="500">Auto-generated behavioral analysis and recommendations</Text>
                <Box bg="white" borderRadius="md" p={4} mb={3}>
                    <Text fontSize="sm" color="gray.700">{ai.paragraph}</Text>
                </Box>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <Box bg="white" borderRadius="md" p={3}>
                        <HStack spacing={1.5} mb={1}><Icon as={FiMessageCircle} color="green.500" boxSize={3.5} /><Text fontSize="xs" color="gray.500" fontWeight="600">Recommended Channel</Text></HStack>
                        <Text fontSize="lg" fontWeight="800" color="green.600">{ai.recommended_channel.value}</Text>
                        <Text fontSize="11px" color="gray.500">{ai.recommended_channel.sub}</Text>
                    </Box>
                    <Box bg="white" borderRadius="md" p={3}>
                        <HStack spacing={1.5} mb={1}><Icon as={FiClock} color="blue.500" boxSize={3.5} /><Text fontSize="xs" color="gray.500" fontWeight="600">Optimal Send Window</Text></HStack>
                        <Text fontSize="lg" fontWeight="800" color="blue.600">{ai.optimal_send_window.value}</Text>
                        <Text fontSize="11px" color="gray.500">{ai.optimal_send_window.sub}</Text>
                    </Box>
                    <Box bg="white" borderRadius="md" p={3}>
                        <HStack spacing={1.5} mb={1}><Icon as={FiTarget} color={toneColor[ai.risk_level.tone]} boxSize={3.5} /><Text fontSize="xs" color="gray.500" fontWeight="600">Risk Level</Text></HStack>
                        <Text fontSize="lg" fontWeight="800" color={toneColor[ai.risk_level.tone]}>{ai.risk_level.value}</Text>
                        <Text fontSize="11px" color="gray.500">{ai.risk_level.sub}</Text>
                    </Box>
                </SimpleGrid>
            </Box>
        </VStack>
    );
}
